import React, { useState } from 'react';

import { getETLComponentType } from '../etl-component-types/factory';

import { TpETLObjectTypes } from '../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunError, TpETLDataContext } from '../interfaces';
import { getDownstreamDependentComponents, getETLComponentWithOutputObj } from '../utils';

import styles from './editETLComponentForm.module.scss';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { TpETLInputObject, TpETLOutputObject } from '../etl-component-types/interfaces';
import { performDryRunETL, getDryRunErrorsForComponent, getETLDryRunObject } from '../etlDryRun';
import { promptConfirmationBox } from '../../../components/message-boxes/ConfirmBox';
import ETLComponentParameter from './ETLComponentParameter';
import { debounceInComponent } from '../../../components/miscUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppSelector } from '../../../app/hooks';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { DataObject } from '../etl-data-types/DataObject';
import { getOutputInputCompatibility } from '../etl-data-types/compatibility';

function renderInput(
  dryRun: TpETLDryRun,
  input: TpETLInputObject,
  objId: string,
  editingComponent: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
  alternativeInput: {
    comp: TpETLPipelineComponent;
    outputType: TpETLOutputObject;
  } | null,
): any {
  const connInfo = getETLComponentWithOutputObj(dryRun.pipeline, objId);
  const inputObject = getETLDryRunObject(dryRun, objId);
  const sourceComp = connInfo.comp;
  let alternativeInputObjectId = null;
  if (alternativeInput) {
    if (inputObject.createdByCompId !== alternativeInput.comp.id)
      if (getOutputInputCompatibility(alternativeInput.outputType, input).isCompatible) {
        // we can't change if the selected component is already providing the current input)
        // can only change to something with a compatible output type
        alternativeInputObjectId = alternativeInput.comp.outputObjectIds[alternativeInput.outputType.id];
        if (!dryRun.objectsMap[alternativeInputObjectId]) alert('object not found');
      }
  }
  return (
    <div key={input.id} className={styles.section}>
      <h3>
        {input.name} <div className={styles.codeTag}>{input.type}</div>
        {alternativeInputObjectId && (
          <button
            onClick={() => {
              if (!dryRun.objectsMap[alternativeInputObjectId]) alert('object not found');
              editingComponent.inputObjectIds[input.id] = alternativeInputObjectId;
              updateEditingComponent(editingComponent);
            }}
          >
            Change to "{alternativeInput?.outputType?.name}" from "{alternativeInput?.comp?.name}"
          </button>
        )}
      </h3>
      <div>
        {!alternativeInputObjectId && (
          <div>
            (In order to change the input of this component, you can select a single component in the pipeline with a
            compatible output)
          </div>
        )}
      </div>
      {input.description && <div>{input.description}</div>}
      <div className={styles.componentTag}>{sourceComp.name}</div>
      <span>{connInfo.outputType.name}</span> &rarr; {input.name}
      <DataObject data={inputObject} />
    </div>
  );
}

function renderAdditionalInputParameter(
  dryRun: TpETLDryRun,
  objId: string,
  paramIdx: number,
  editingComponent: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const obj = getETLDryRunObject(dryRun, objId);
  const connInfo = getETLComponentWithOutputObj(dryRun.pipeline, objId);
  const sourceComp = connInfo.comp;
  return (
    <div key={`$param${paramIdx + 1}`}>
      <div className={styles.componentTag}>{sourceComp.name}</div>
      <span>{connInfo.outputType.name}</span> &rarr; {`$param${paramIdx + 1}`}
      <div className={styles.codeTag}>{obj.type}</div>
      <button
        onClick={() => {
          editingComponent.extraParamObjectIds = editingComponent.extraParamObjectIds.filter((obj) => obj !== objId);
          updateEditingComponent(editingComponent);
        }}
      >
        <FontAwesomeIcon icon={['fas', 'xmark']} />
      </button>
    </div>
  );
}

function renderOutput(dryRun: TpETLDryRun, output: TpETLOutputObject, objId: string): any {
  const outputObject = getETLDryRunObject(dryRun, objId);
  return (
    <div key={output.id} className={styles.section}>
      <h3>
        {output.name} <div className={styles.codeTag}>{output.type}</div>
      </h3>
      {output.description && <div>{output.description}</div>}
      <DataObject data={outputObject} />
    </div>
  );
}

interface TpEditETLComponentForm {
  inpDryRun: TpETLDryRun;
  inpComponent: TpETLPipelineComponent;
  selectedOutputIds: string[];
  updateComponent: (newComp: TpETLPipelineComponent) => any;
  deleteComponent: () => any;
  showLastResults: (() => void) | null;
  showLastLogResults: (() => void) | null;
  downloadLastResults: (() => void) | null;
  assetETLDataContext: TpETLDataContext | null;
}

function EditETLComponentForm({
  inpDryRun,
  inpComponent,
  selectedOutputIds,
  updateComponent,
  deleteComponent,
  showLastResults,
  showLastLogResults,
  downloadLastResults,
  assetETLDataContext,
}: TpEditETLComponentForm) {
  const pipeline = inpDryRun.pipeline;
  const [editingDryRun, setEditingDryRun] = useState<TpETLDryRun>(structuredClone(inpDryRun));
  const [editingComponent, setEditingComponent] = useState<TpETLPipelineComponent>(structuredClone(inpComponent));

  const compType = getETLComponentType(editingComponent.componentType);
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);

  const nonSpecificErrors = getDryRunErrorsForComponent(editingDryRun, editingComponent.id).filter(
    (error) => !error.paramId,
  );
  // We use this variable to keep track of a possible alternative input, based on the current selection in the pipeline
  let alternativeInput =
    selectedOutputIds.length == 1 ? getETLComponentWithOutputObj(pipeline, selectedOutputIds[0]) : null;
  // cannot assign input to output of component itself
  if (alternativeInput && alternativeInput.comp.id == editingComponent.id) alternativeInput = null;
  // avoid circular dependencies
  if (
    alternativeInput &&
    getDownstreamDependentComponents(pipeline, editingComponent.id).filter(
      (comp) => comp.id == alternativeInput?.comp?.id,
    ).length > 0
  )
    alternativeInput = null;

  let alternativeAdditionalParamObjId = null;
  if (alternativeInput) {
    if (alternativeInput.outputType.type == TpETLObjectTypes.TEXT) {
      // It should be text output
      alternativeAdditionalParamObjId = alternativeInput.comp.outputObjectIds[alternativeInput.outputType.id];
      if (editingComponent.extraParamObjectIds.indexOf(alternativeAdditionalParamObjId) >= 0) {
        // Input should not already be used as additional parameter
        alternativeAdditionalParamObjId = null;
      }
    }
  }

  function getUpdatedEditingDryRun(): TpETLDryRun {
    // Returns an updated dry run object, reflecting the edited state of the component
    // we temporary clone the pipeline, and replace the component with the modified one in order to do a dry run
    const testPipeline = structuredClone(pipeline);
    const compIdx = testPipeline.components.findIndex((cmp) => cmp.id == editingComponent.id);
    if (compIdx < 0) throw `Component not found: ${editingComponent.id}`;
    testPipeline.components[compIdx] = editingComponent;
    testPipeline.componentMap[editingComponent.id] = editingComponent;
    return performDryRunETL(testPipeline, assetETLDataContext);
  }

  const debouncedUpdateDryRun = debounceInComponent(() => {
    setEditingDryRun(getUpdatedEditingDryRun());
  }, 500);

  function getErrorsWithModifiedComponent(): TpETLDryRunError[] {
    const testDryRun = getUpdatedEditingDryRun();
    return getDryRunErrorsForComponent(testDryRun, editingComponent.id);
  }

  function updateEditingComponent(updatedComp: TpETLPipelineComponent) {
    setEditingComponent({ ...updatedComp });
    debouncedUpdateDryRun();
  }

  function onClickedUpdateComponent() {
    const errors = getErrorsWithModifiedComponent();
    if (errors.length == 0) {
      updateComponent(editingComponent);
    } else {
      const errorString = errors.map((error) => error.message).join('<br />');
      promptConfirmationBox({
        title: 'Error',
        description: `Component has the following error(s). Are you sure you want to continue?<br /><br />${errorString}`,
        warningAlert: true,
      }).then((confirmed) => {
        updateComponent(editingComponent);
      });
    }
  }

  function promptDeleteComponent() {
    const dependentComps = getDownstreamDependentComponents(pipeline, editingComponent.id);
    let description = 'Are you sure you want to delete this component?';
    if (dependentComps.length > 0)
      description += `<br /><br />WARNING: this will also remove ${dependentComps.length} downstream component(s) that depend on this one!`;
    promptConfirmationBox({
      title: 'Delete component',
      description,
      warningAlert: true,
    }).then((confirmed) => {
      if (confirmed) {
        deleteComponent();
      }
    });
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>{editingComponent.name}</div>
        <div className={styles.headerButtons}>
          <FancyButton iconName="check" onClick={onClickedUpdateComponent}>
            Update component
          </FancyButton>
          <div style={{ display: 'inline-block', width: '15px' }} />
          <FancyButton iconName="trash-can" onClick={promptDeleteComponent}>
            Delete component...
          </FancyButton>
        </div>
      </div>
      <div className={styles.body}>
        {showLastLogResults ? (
          <>
            <FancyButton iconName="file-lines" onClick={showLastLogResults}>
              Download Latest Logs
            </FancyButton>
            <div style={{ display: 'inline-block', width: '15px' }} />
          </>
        ) : (
          <div style={{ display: 'inline-block', width: '195px' }} />
        )}
        {downloadLastResults ? (
          <>
            <FancyButton iconName="download" onClick={downloadLastResults}>
              Download Last Results
            </FancyButton>
            <div style={{ display: 'inline-block', width: '15px' }} />
          </>
        ) : (
          <div style={{ display: 'inline-block', width: '195px' }} />
        )}
        {showLastResults ? (
          <>
            <FancyButton iconName="table" onClick={showLastResults}>
              Show Last Results
            </FancyButton>
            <div style={{ display: 'inline-block', width: '15px' }} />
          </>
        ) : (
          <div style={{ display: 'inline-block', width: '195px' }} />
        )}
        {nonSpecificErrors.length > 0 && (
          <div className={styles.errors}>
            {nonSpecificErrors.map((error, idx) => (
              <div key={`idx_${idx}`}>{error.message}</div>
            ))}
          </div>
        )}
        <h2>Component Description</h2>
        Component name:
        <br />
        <input
          style={{ width: '250px' }}
          value={editingComponent.name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            editingComponent.name = event.target.value;
            setEditingComponent({ ...editingComponent });
          }}
        />
        <p>
          <b>{compType.name}</b> {compType.description}
        </p>
        <h2>Input Object(s)</h2>
        {compType.inputs.map((input) =>
          renderInput(
            editingDryRun,
            input,
            editingComponent.inputObjectIds[input.id],
            editingComponent,
            (updatedComp: TpETLPipelineComponent) => {
              setEditingComponent({ ...updatedComp });
              debouncedUpdateDryRun();
            },
            alternativeInput,
          ),
        )}
        <h2>Additional Input Parameters</h2>
        {editingComponent.extraParamObjectIds.length == 0 && <div>None</div>}
        {editingComponent.extraParamObjectIds.map((objId, paramIdx) =>
          renderAdditionalInputParameter(editingDryRun, objId, paramIdx, editingComponent, updateEditingComponent),
        )}
        {alternativeAdditionalParamObjId && (
          <button
            onClick={() => {
              if (!editingDryRun.objectsMap[alternativeAdditionalParamObjId]) alert('object not found');
              editingComponent.extraParamObjectIds.push(alternativeAdditionalParamObjId);
              updateEditingComponent(editingComponent);
            }}
          >
            Add "{alternativeInput?.outputType?.name}" from "{alternativeInput?.comp?.name}"
          </button>
        )}
        <h2>Configuration</h2>
        {compType.params.map((param) => (
          <div key={param.id} className={styles.section}>
            <ETLComponentParameter
              dryRun={editingDryRun}
              comp={editingComponent}
              paramDef={param}
              updateParam={(paramId: string, newValue: any) => {
                editingComponent.params[paramId] = newValue;
                updateEditingComponent(editingComponent);
              }}
            />
          </div>
        ))}
        {compType.renderCustomConfigParams &&
          (compType.useOntology && ontology
            ? compType.renderCustomConfigParams(
                editingDryRun,
                editingComponent,
                updateEditingComponent,
                ontology,
                assetETLDataContext,
              )
            : compType.renderCustomConfigParams(
                editingDryRun,
                editingComponent,
                updateEditingComponent,
                undefined,
                assetETLDataContext,
              ))}
        <h2>Output Object(s)</h2>
        {compType.outputs.map((output) =>
          renderOutput(editingDryRun, output, editingComponent.outputObjectIds[output.id]),
        )}
      </div>
    </div>
  );
}

export default EditETLComponentForm;
